import { logger } from '@springtree/eva-sdk-core-logger';
import { Core } from '@springtree/eva-services-core';
import { IEvaServiceCallOptions, bootstrapEndpoint } from '@springtree/eva-sdk-core-service';
// tslint:disable-next-line: import-name
import ky from 'ky-universal';
import { createLogic } from 'redux-logic';
import {
  createServiceRequestAction,
  IEvaServiceErrorActionPayload,
  IEvaServiceRequestAction,
  IShoppingCartAddAction,
  IShoppingCartModifyAction,
} from '../action';
import { createGetShoppingCartWithStatusLogic, GetShoppingCartWithStatus } from '../helpers/get-shopping-cart-with-status';
import { getServicePromise } from '../promise';
import { IEvaReduxStoreShoppingCartShape } from '../state/shopping-cart';
import { IEvaReduxStoreDefaultShape } from '../store';

/**
 * Creates all the EVA shopping cart logic
 *
 * @export
 * @template SVC
 * @param {new () => SVC} serviceDefinition The EVA Service registry definition
 * @param {string} [stateKey] Optional state key name if not the same as the service name
 * @returns {Logic<IEvaServiceState<SVC>, any, any, any, any, string>}
 */
export function createShoppingCartLogic<SHAPE extends IEvaReduxStoreDefaultShape>(
) {
  // Create the service request logics
  // These will handle the normal side effects of actually calling the EVA services
  // and its response and error handling
  //
  const shoppingCartLogics = createGetShoppingCartWithStatusLogic({ stateKey: 'shoppingCart' });

  // This piece of logic will listen for the explicit cart update action and will update the
  // shopping cart if there is a current order id
  //
  const shoppingCartUpdateLogic = createLogic<SHAPE, IEvaReduxStoreShoppingCartShape, any, any, any, string>({
    type: 'shoppingCart:UPDATE',
    processOptions: {
      dispatchReturn: true,
    },
    async process({ getState, action }) {
      const storeState: SHAPE = getState();
      const updateCartAction = action as IEvaServiceRequestAction<GetShoppingCartWithStatus, 'shoppingCart:UPDATE'>;
      const payload = updateCartAction.payload;
      const servicePromise = getServicePromise(payload.serviceContext);

      // Collect the needed information from the current shopping cart
      // If we have no current order this action will be result in no action being taken.
      //
      const currentShoppingCart = storeState.shoppingCart;
      const currentOrderId = currentShoppingCart?.data.response?.order.ShoppingCart.ID;
      const requestedOrderId = payload.data?.order.OrderID || currentOrderId;

      if (requestedOrderId) {
        servicePromise.resolve();

        // Prepare the shopping cart with status action update request inserting
        // the current order id as the requested order
        //
        const shoppingCartAction = createServiceRequestAction({
          service: GetShoppingCartWithStatus,
          stateOptions: {
            stateKey: 'shoppingCart',
          },
          payload: { order: { OrderID: requestedOrderId } },
          chainContext: payload.chainContext,
        });

        // Return the action that will trigger the cart with status update
        //
        return shoppingCartAction;
      }

      const noCartFailure = new Error('No current shopping cart to update');
      servicePromise.reject(noCartFailure);
      throw noCartFailure;
    },
  });

  // This piece of logic will listen for the prepare action and will update the
  // shopping cart on success
  //
  const shoppingCartPrepareForCheckoutLogic = createLogic<SHAPE, IEvaReduxStoreShoppingCartShape, any, any, any, string>({
    type: 'shoppingCart:PREPARE_CHECKOUT',
    processOptions: {
      dispatchReturn: true,
    },
    async process({ getState, action }) {
      const storeState: SHAPE = getState();
      const prepareCartAction = action as IEvaServiceRequestAction<Core.PrepareOrderForCheckout, 'shoppingCart:PREPARE_CHECKOUT'>;
      const payload = prepareCartAction.payload;
      const endpointUrl = payload.endpoint || storeState.endpointUrl;
      const servicePromise = getServicePromise(payload.serviceContext);

      // Collect the needed information from the current shopping cart
      // If we have no current order this action will be result in no action being taken.
      //
      const currentShoppingCart = storeState.shoppingCart;
      const currentOrderId = currentShoppingCart.data.response?.order.ShoppingCart.ID;

      // Ensure we have an endpoint to use
      //
      if (!endpointUrl) {
        const failure = new Error('No EVA endpoint provided for service call');
        servicePromise.reject(failure);
        throw failure;
      }

      if (currentOrderId) {

        // Bootstrapping the store to get the EvaEndpoint instance we need
        // The SDK caches bootstrap results so after the first call this will be instant
        //
        const endpoint = await bootstrapEndpoint({ uri: endpointUrl });

        const serviceName = new Core.PrepareOrderForCheckout().name;
        const serviceOptions: IEvaServiceCallOptions = Object.assign(
          {
            serviceContext: payload.serviceContext,
            chainContext: payload.chainContext,
          },
          payload.serviceOptions,
        );

        // Set the current user authentication token if needed
        //
        const currentUserToken = storeState.currentUserToken;
        if (!serviceOptions.authenticationToken && currentUserToken) {
          serviceOptions.authenticationToken = currentUserToken;
        }

        // Set the current application token if needed
        //
        const currentApplicationToken = storeState.currentApplicationToken;
        if (!serviceOptions.applicationToken && currentApplicationToken) {
          serviceOptions.applicationToken = currentApplicationToken;
        }

        // Call the prepare order service
        //
        logger.debug(`[EVA:SDK:LOGIC:shoppingCart] Call service ${serviceName}`, serviceOptions)();

        try {
          servicePromise.resolve(await endpoint.callService(
            Core.PrepareOrderForCheckout,
            {
              OrderID: currentOrderId,
            },
            serviceOptions,
          ));

          // Prepare the shopping cart with status action update request inserting
          // the current order id as the requested order
          // Ensure we use the same chain context from the cart modifying services
          //
          const shoppingCartAction = createServiceRequestAction({
            service: GetShoppingCartWithStatus,
            stateOptions: {
              stateKey: 'shoppingCart',
            },
            payload: { order: { OrderID: currentOrderId } },
            chainContext: payload.chainContext,
          });

          // Return the action that will trigger the cart with status update
          //
          return shoppingCartAction;
        } catch (error) {
          logger.error(`[EVA:SDK:LOGIC:shoppingCart] Error calling service ${serviceName}`, error)();

          const errorPayload: IEvaServiceErrorActionPayload<GetShoppingCartWithStatus> = {
            serviceContext: serviceOptions.serviceContext as string,
            chainContext: serviceOptions.chainContext as string,
            data: error.message,
          };

          // If this an HTTPError from fetch we can try to extract the JSON response
          //
          if (error instanceof ky.HTTPError) {
            try {
              const httpError: ky.HTTPError = error;
              const jsonError = await httpError.response.json();
              errorPayload.data = jsonError || error.message;
              errorPayload.httpResponse = error.response.status;
              const headers: Record<string, string> = {};
              error.response.headers.forEach((value, key) => (headers[key.toLowerCase()] = value));
              errorPayload.headers = headers;
            } catch (jsonParseError) {
              logger.warn('[EVA:SDK:LOGIC:shoppingCart] Failed to parse JSON error response', jsonParseError)();
            }
          }

          servicePromise.reject(errorPayload);
          throw errorPayload;
        }
      }

      const noCartFailure = new Error('No current shopping cart to prepare');
      servicePromise.reject(noCartFailure);
      throw noCartFailure;
    },
  });

  // This piece of logic handles the adding of products to the current shopping
  // cart. It will create a new order if one is not currently set.
  // It will also check if the shopping cart is in a non-completed state
  //
  const shoppingCartAddLogic = createLogic<SHAPE, IEvaReduxStoreShoppingCartShape, any, any, any, string>({
    type: 'shoppingCart:ADD',
    processOptions: {
      dispatchReturn: true,
    },
    async process({ getState, action }) {
      const storeState: SHAPE = getState();
      const addToCartAction = action as IShoppingCartAddAction;
      const payload = addToCartAction.payload;
      const endpointUrl = payload.endpoint || storeState.endpointUrl;
      const servicePromise = getServicePromise(payload.serviceContext);

      // Collect the needed information from the current shopping cart
      // We will use this to determine if we need to call CreateOrder
      // The action additional data will contain the order type for creating
      // said order if it is not the default type of Sales
      //
      const currentShoppingCart = storeState.shoppingCart;
      const currentOrderId = currentShoppingCart.data.response?.order.ShoppingCart.ID;
      const isCompleted = currentShoppingCart.data.response?.order.ShoppingCart.IsCompleted === true;
      const orderType = payload.orderType || EVA.Core.OrderTypes.Sales;

      // Ensure we have an endpoint to use
      //
      if (!endpointUrl) {
        const failure = new Error('No EVA endpoint provided for service call');
        servicePromise.reject(failure);
        throw failure;
      }

      // Bootstrapping the store to get the EvaEndpoint instance we need
      // The SDK caches bootstrap results so after the first call this will be instant
      //
      const endpoint = await bootstrapEndpoint({ uri: endpointUrl });
      const serviceName = new addToCartAction.payload.service().name;
      const serviceOptions: IEvaServiceCallOptions = Object.assign(
        {
          serviceContext: payload.serviceContext,
          chainContext: payload.chainContext,
        },
        payload.serviceOptions,
      );
      logger.debug(`[EVA:SDK:LOGIC:shoppingCart] Preparing to call service ${serviceName}`, serviceOptions)();

      // Set the current user authentication token if needed
      //
      const currentUserToken = storeState.currentUserToken;
      if (!serviceOptions.authenticationToken && currentUserToken) {
        serviceOptions.authenticationToken = currentUserToken;
      }

      // Set the current application token if needed
      //
      const currentApplicationToken = storeState.currentApplicationToken;
      if (!serviceOptions.applicationToken && currentApplicationToken) {
        serviceOptions.applicationToken = currentApplicationToken;
      }

      // Check if we need to create a new order
      //
      let newOrderId: number;
      if (!currentOrderId || isCompleted) {
        logger.debug(`[EVA:SDK:LOGIC:shoppingCart] Creating order before calling service ${serviceName}`, serviceOptions)();
        const newOrder = await endpoint.callService(
          Core.CreateOrder,
          {
            Type: orderType,
          },
          {
            ...serviceOptions,
            serviceContext: undefined, // Generates a new UUID, each individual call must be unique
          },
        ) as EVA.Core.CreateOrderResponse;
        newOrderId = newOrder.OrderID;
      } else {
        newOrderId = currentOrderId;
      }

      // Prepare the call payload for the services used to add something to the
      // cart. Depending on which service is called we may need to insert the
      // current order id in different places
      //
      const requestPayload: any = {
        ...addToCartAction.payload.data,
      };
      if (serviceName.match('AddWishListProductToOrder') || serviceName.match('AddWishListFundingToOrder')) {
        (requestPayload as Record<string, number>).TargetOrderID = newOrderId;
      } else {
        (requestPayload as Record<string, number>).OrderID = newOrderId;
      }

      // Call the add to cart service
      //
      logger.debug(`[EVA:SDK:LOGIC:shoppingCart] Call service ${serviceName}`, serviceOptions)();
      try {
        servicePromise.resolve(await endpoint.callService(
          addToCartAction.payload.service,
          requestPayload,
          serviceOptions,
        ));

        // Prepare the shopping cart with status action update request inserting
        // the new order id as the requested order
        // Ensure we use the same chain context from the create/addToCart services
        //
        const shoppingCartAction = createServiceRequestAction({
          service: GetShoppingCartWithStatus,
          stateOptions: {
            stateKey: 'shoppingCart',
          },
          payload: { order: { OrderID: newOrderId } },
          chainContext: payload.chainContext,
        });

        // Return the action that will trigger the cart with status update
        //
        return shoppingCartAction;
      } catch (error) {
        logger.error(`[EVA:SDK:LOGIC:shoppingCart] Error calling service ${serviceName}`, error)();

        const errorPayload: IEvaServiceErrorActionPayload<GetShoppingCartWithStatus> = {
          serviceContext: serviceOptions.serviceContext as string,
          chainContext: serviceOptions.chainContext as string,
          data: error.message,
        };

        // If this an HTTPError from fetch we can try to extract the JSON response
        //
        if (error instanceof ky.HTTPError) {
          try {
            const httpError: ky.HTTPError = error;
            const jsonError = await httpError.response.json();
            errorPayload.data = jsonError || error.message;
            errorPayload.httpResponse = error.response.status;
            const headers: Record<string, string> = {};
            error.response.headers.forEach((value, key) => (headers[key.toLowerCase()] = value));
            errorPayload.headers = headers;
          } catch (jsonParseError) {
            logger.warn('[EVA:SDK:LOGIC:shoppingCart] Failed to parse JSON error response', jsonParseError)();
          }
        }

        servicePromise.reject(errorPayload);
        throw errorPayload;
      }
    },
  });

  // This piece of logic handles modifications to the shopping cart and will
  // trigger an update of the shopping cart when completed
  //
  const shoppingCartModifyLogic = createLogic<SHAPE, IEvaReduxStoreShoppingCartShape, any, any, any, string>({
    type: 'shoppingCart:MODIFY',
    processOptions: {
      dispatchReturn: true,
    },
    async process({ getState, action }) {
      const storeState: SHAPE = getState();
      const modifyCartAction = action as IShoppingCartModifyAction;
      const payload = modifyCartAction.payload;
      const endpointUrl = payload.endpoint || storeState.endpointUrl;
      const servicePromise = getServicePromise(payload.serviceContext);

      // Collect the needed information from the current shopping cart
      // If we have no current order this action will be result in no action being taken.
      //
      const currentShoppingCart = storeState.shoppingCart;
      const currentOrderId = currentShoppingCart.data.response?.order.ShoppingCart.ID;

      // Ensure we have an endpoint to use
      //
      if (!endpointUrl) {
        const failure = new Error('No EVA endpoint provided for service call');
        servicePromise.reject(failure);
        throw failure;
      }

      if (currentOrderId) {
        // Bootstrapping the store to get the EvaEndpoint instance we need
        // The SDK caches bootstrap results so after the first call this will be instant
        //
        const endpoint = await bootstrapEndpoint({ uri: endpointUrl });
        const serviceName = new modifyCartAction.payload.service().name;
        const serviceOptions: IEvaServiceCallOptions = Object.assign(
          {
            serviceContext: payload.serviceContext,
            chainContext: payload.chainContext,
          },
          payload.serviceOptions,
        );
        logger.debug(`[EVA:SDK:LOGIC:shoppingCart] Preparing to call service ${serviceName}`, serviceOptions)();

        // Set the current user authentication token if needed
        //
        const currentUserToken = storeState.currentUserToken;
        if (!serviceOptions.authenticationToken && currentUserToken) {
          serviceOptions.authenticationToken = currentUserToken;
        }

        // Set the current application token if needed
        //
        const currentApplicationToken = storeState.currentApplicationToken;
        if (!serviceOptions.applicationToken && currentApplicationToken) {
          serviceOptions.applicationToken = currentApplicationToken;
        }

        // Prepare the call payload for the services used to modify something in the
        // cart. Depending on which service is called we may need to insert the
        // current order id in different places
        //
        const requestPayload: any = {
          ...modifyCartAction.payload.data,
        };

        // Some services need the OrderID added to their payload
        //
        const servicesRequiringOrderID = [
          'Core:AttachCustomerToOrder',
          'Core:CancelOrder',
          'Core:ChangeOrderLinesToCarryOut',
          'Core:ChangeOrderLinesToDelivery',
          'Core:ChangeOrderLinesToPickup',
          'Core:CreateCustomer',
          'Core:DetachCustomerFromOrder',
          'Core:PrepareOrderForCheckout',
          'Core:SetGiftWrappingOptionsOnOrder',
          'Core:UpdateOrderBillingAddress',
          'Core:UpdateOrderShippingAddress',
        ];
        if (servicesRequiringOrderID.indexOf(serviceName) !== -1) {
          (requestPayload as Record<string, number>).OrderID = currentOrderId;
        }

        // Call the modifying cart service
        //
        logger.debug(`[EVA:SDK:LOGIC:shoppingCart] Call service ${serviceName}`, serviceOptions)();

        try {
          servicePromise.resolve(await endpoint.callService(
            modifyCartAction.payload.service,
            requestPayload,
            serviceOptions,
          ));

          // Prepare the shopping cart with status action update request inserting
          // the current order id as the requested order
          // Ensure we use the same chain context from the cart modifying services
          //
          const shoppingCartAction = createServiceRequestAction({
            service: GetShoppingCartWithStatus,
            stateOptions: {
              stateKey: 'shoppingCart',
            },
            payload: { order: { OrderID: currentOrderId } },
            chainContext: payload.chainContext,
          });

          // Return the action that will trigger the cart with status update
          //
          return shoppingCartAction;
        } catch (error) {
          logger.error(`[EVA:SDK:LOGIC:shoppingCart] Error calling service ${serviceName}`, error)();

          const errorPayload: IEvaServiceErrorActionPayload<GetShoppingCartWithStatus> = {
            serviceContext: serviceOptions.serviceContext as string,
            chainContext: serviceOptions.chainContext as string,
            data: error.message,
          };

          // If this an HTTPError from fetch we can try to extract the JSON response
          //
          if (error instanceof ky.HTTPError) {
            try {
              const httpError: ky.HTTPError = error;
              const jsonError = await httpError.response.json();
              errorPayload.data = jsonError || error.message;
              errorPayload.httpResponse = error.response.status;
              const headers: Record<string, string> = {};
              error.response.headers.forEach((value, key) => (headers[key.toLowerCase()] = value));
              errorPayload.headers = headers;
            } catch (jsonParseError) {
              logger.warn('[EVA:SDK:LOGIC:shoppingCart] Failed to parse JSON error response', jsonParseError)();
            }
          }

          servicePromise.reject(errorPayload);
          throw errorPayload;
        }
      }

      const noCartFailure = new Error('No current shopping cart to modify');
      servicePromise.reject(noCartFailure);
      throw noCartFailure;
    },
  });

  return [
    ...shoppingCartLogics,
    shoppingCartAddLogic,
    shoppingCartModifyLogic,
    shoppingCartPrepareForCheckoutLogic,
    shoppingCartUpdateLogic,
  ];
}
